/**
 * Client for POST /api/v2/life-story-refinements (TK-3204).
 *
 * A raw fetch rather than redux-json-api: the result is never a resource the
 * store should hold (the refined text goes straight into the textarea and the
 * story's own autosave persists it), and the request has to be abortable so
 * Stop can discard a rewrite mid-flight.
 */

/**
 * The API's modes. The first four rewrite; `questions` returns the Enrich
 * interview's follow-up questions, `weave` writes the answers into the story,
 * and `talking_points` returns a short list to record from.
 */
export const API_MODES = {
  POLISH: 'polish',
  SHORTEN: 'shorten',
  ENRICH: 'enrich',
  CUSTOM: 'custom',
  QUESTIONS: 'questions',
  WEAVE: 'weave',
  TALKING_POINTS: 'talking_points'
}

// Modes whose result is the rewritten story; the others return lists.
const REWRITE_MODES = [API_MODES.POLISH, API_MODES.SHORTEN, API_MODES.ENRICH, API_MODES.CUSTOM, API_MODES.WEAVE]

export const REFINE_ERROR_CODES = {
  CANCELLED: 'cancelled',
  UNAUTHORIZED: 'unauthorized',
  RATE_LIMITED: 'rate_limited',
  REFUSED: 'refused',
  TOO_SHORT: 'too_short',
  TOO_LONG: 'too_long',
  NO_ROOM: 'no_room',
  PROVIDER_ERROR: 'provider_error',
  NETWORK: 'network',
  UPSTREAM_ERROR: 'upstream_error'
}

const JSON_API_HEADERS = {
  Accept: 'application/vnd.api+json',
  'Content-Type': 'application/vnd.api+json'
}

export class RefineError extends Error {
  constructor(code, message) {
    super(message || code)
    this.name = 'RefineError'
    this.code = code
  }
}

const isAbort = error => Boolean(error && (error.name === 'AbortError' || error.code === REFINE_ERROR_CODES.CANCELLED))

/** Maps a non-2xx JSON:API response to a RefineError, reading the API's machine-readable code when it sent one. */
const errorFromResponse = (response, body) => {
  const firstError = body && Array.isArray(body.errors) && body.errors[0]
  const apiCode = firstError && ((firstError.meta && firstError.meta.code) || firstError.code)

  if (response.status === 401 || response.status === 403) return new RefineError(REFINE_ERROR_CODES.UNAUTHORIZED)
  if (response.status === 429 || apiCode === REFINE_ERROR_CODES.RATE_LIMITED) return new RefineError(REFINE_ERROR_CODES.RATE_LIMITED)

  if (apiCode && Object.values(REFINE_ERROR_CODES).includes(apiCode))
    return new RefineError(apiCode, firstError.title || firstError.detail)

  return new RefineError(REFINE_ERROR_CODES.UPSTREAM_ERROR, `Refine request failed with status ${response.status}`)
}

const parseBody = response => response.json().catch(() => null)

const stringList = list => (Array.isArray(list) ? list.filter(entry => typeof entry === 'string' && entry.trim()) : [])

const toInterviewQuestions = list => (
  Array.isArray(list) ?
    list
      .filter(entry => entry && typeof entry.text === 'string' && entry.text.trim())
      .map(entry => ({
        text: entry.text,
        quote: typeof entry.quote === 'string' ? entry.quote : '',
        examples: stringList(entry.examples)
      })) :
    []
)

/**
 * Sends one story (or passage) to be refined. Resolves with
 * `{refinedText, questions, interviewQuestions, marks, talkingPoints, heldBack, model}`
 * (the lists are empty when the mode does not produce them; `heldBack` is
 * true when the model left part of a custom request undone rather than
 * invent facts, TK-3268); rejects with a
 * RefineError whose `code` picks the note the writer sees. Story text is
 * only ever sent here, on the writer's click.
 *
 * `chapter` / `chapters` tell the model where the prompt sits in the life
 * story; `answers` (`[{question, quote, answer}]`) are the interview's
 * answers for the `weave` mode.
 */
export const refineLifeStory = ({attributes, signal, axiosConfig}) => {
  if (!axiosConfig || !axiosConfig.baseURL)
    return Promise.reject(new RefineError(REFINE_ERROR_CODES.NETWORK, 'API endpoint is not configured'))

  const body = JSON.stringify({
    data: {
      type: 'life-story-refinements',
      attributes: {
        mode: attributes.mode,
        question: attributes.question,
        text: attributes.text,
        ...attributes.instruction ? {instruction: attributes.instruction} : {},
        ...attributes.passage ? {passage: attributes.passage} : {},
        ...attributes.regenerate ? {regenerate: true} : {},
        ...attributes.chapter ? {chapter: attributes.chapter} : {},
        ...attributes.chapters && attributes.chapters.length ? {chapters: attributes.chapters} : {},
        ...attributes.answers && attributes.answers.length ? {answers: attributes.answers} : {}
      }
    }
  })

  return fetch(`${axiosConfig.baseURL}/life-story-refinements`, {
    method: 'POST',
    headers: {...axiosConfig.headers, ...JSON_API_HEADERS},
    body,
    ...signal ? {signal} : {}
  })
    .then(response => parseBody(response).then(json => {
      if (!response.ok) throw errorFromResponse(response, json)

      const result = (json && json.data && json.data.attributes) || {}
      const refinedText = typeof result['refined-text'] === 'string' ? result['refined-text'] : ''

      if (REWRITE_MODES.includes(attributes.mode) && !refinedText.trim())
        throw new RefineError(REFINE_ERROR_CODES.UPSTREAM_ERROR, 'Refine response had no text')

      return {
        refinedText,
        questions: Array.isArray(result.questions) ?
          result.questions.filter(question => (
            question && typeof question.text === 'string' && (!question.anchor || typeof question.anchor === 'string')
          )) :
          [],
        interviewQuestions: toInterviewQuestions(result['interview-questions']),
        marks: stringList(result.marks),
        talkingPoints: stringList(result['talking-points']),
        heldBack: result['held-back'] === true,
        model: result.model || null
      }
    }))
    .catch(error => {
      if (isAbort(error)) throw new RefineError(REFINE_ERROR_CODES.CANCELLED)
      if (error instanceof RefineError) throw error

      throw new RefineError(REFINE_ERROR_CODES.NETWORK, error && error.message)
    })
}
